/** Resume the chat a subscriber left to grant repository investigation in Mission Control.
 * MC's ocn-chatbox callback returns the browser to a FIXED url (MC env OCN_CHATBOX_RETURN_URL =
 * https://portal.ocnexus.dev/?page=subscriber-chat&codex_consent=1), which cannot name the chat. Before
 * leaving, the chat stores its own route in sessionStorage (same tab; same pattern as mcp-auth-return.ts);
 * on return the shell consumes it once and navigates back. Nothing here starts a run. */
const RETURN_KEY = 'ocn.codex.consent-return';
const RECHECK_KEY = 'ocn.codex.consent-recheck';
export const CODEX_CONSENT_RETURN_TTL_MS = 15 * 60_000;
const RECHECK_TTL_MS = 60_000;
const ROUTE = /^subscriber-chat:(?:chat|project):[A-Za-z0-9_-]{1,128}$/;

type Marker = { route: string; userKey: string; expiresAt: number };

function storage(): Storage | null {
  try { return typeof sessionStorage === 'undefined' ? null : sessionStorage; } catch { return null; }
}

/** Single use: always removes the entry; returns it only when well formed, unexpired and for this user. */
function take(key: string, userKey: string, now: number, ttl: number): Marker | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    store.removeItem(key);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const m = value as Record<string, unknown>;
    if (typeof m.route !== 'string' || !ROUTE.test(m.route) || typeof m.userKey !== 'string' || m.userKey !== userKey
      || typeof m.expiresAt !== 'number' || m.expiresAt <= now || m.expiresAt > now + ttl) return null;
    return { route: m.route, userKey: m.userKey, expiresAt: m.expiresAt };
  } catch { return null; }
}

function put(key: string, marker: Marker): void {
  const store = storage();
  if (!store) return;
  try { store.setItem(key, JSON.stringify(marker)); } catch { /* storage unavailable: the user navigates back manually */ }
}

/** The dashboard route of the chat that is open: its project thread, else the explicit chat. */
export function codexChatRoute(projectId: string | null, chatSessionId: string | null): string | null {
  const route = projectId ? `subscriber-chat:project:${projectId}` : chatSessionId ? `subscriber-chat:chat:${chatSessionId}` : null;
  return route && ROUTE.test(route) ? route : null;
}

/** Called right before the browser leaves for Mission Control consent. */
export function rememberCodexConsentReturn(route: string, userKey: string, now = Date.now()): void {
  if (!ROUTE.test(route) || !userKey) return;
  put(RETURN_KEY, { route, userKey, expiresAt: now + CODEX_CONSENT_RETURN_TTL_MS });
}

export type CodexConsentReturn = Readonly<{ route: string | null; search: string }>;

/** On load: null unless the URL is the consent return (`?page=subscriber-chat&codex_consent=1`). Otherwise
 * consumes the marker (single use) and returns the saved route for this user (null when missing, expired
 * or another user's) plus the query string with `codex_consent` stripped. A resumed route also arms one
 * grant re-check for that chat. */
export function resolveCodexConsentReturn(search: string, userKey: string, now = Date.now()): CodexConsentReturn | null {
  const params = new URLSearchParams(search);
  if (params.get('page') !== 'subscriber-chat' || params.get('codex_consent') !== '1') return null;
  params.delete('codex_consent');
  const rest = params.toString();
  const marker = userKey ? take(RETURN_KEY, userKey, now, CODEX_CONSENT_RETURN_TTL_MS) : null;
  if (marker) put(RECHECK_KEY, { route: marker.route, userKey, expiresAt: now + RECHECK_TTL_MS });
  return { route: marker?.route ?? null, search: rest ? `?${rest}` : '' };
}

/** Called once by a mounted chat: true (single use) when it is the resumed chat and should re-check its
 * grant. Another chat (e.g. the plain page briefly shown before navigation) leaves the marker in place. */
export function takeCodexConsentRecheck(userKey: string, route: string, now = Date.now()): boolean {
  const store = storage();
  if (!store) return false;
  try {
    const raw = store.getItem(RECHECK_KEY);
    if (!raw) return false;
    const value: unknown = JSON.parse(raw);
    const routeOf = value && typeof value === 'object' ? (value as { route?: unknown }).route : undefined;
    if (routeOf !== route) return false;
  } catch { return false; }
  return take(RECHECK_KEY, userKey, now, RECHECK_TTL_MS) !== null;
}
